import { useCallback, useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Dialog } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { TaskActivityDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'

import { PossibleTaskState, TaskFooterComponent } from 'modules/inbox_tasks/lib/abstraction'
import { trackCloseTask, trackGoToTaskDetail } from 'modules/inbox_tasks/lib/events'
import {
  getStateForTaskDefinition,
  getTaskTitle,
  getTaskableForTypename,
  useTaskDefinition,
} from 'modules/inbox_tasks/lib/helpers'

import Content from './Content'
import { getMetadata, getModule, getStatusTag, useModuleHref } from './utils'

type Data = {
  id: string
}

const CLOSE_TIMEOUT = 100

export const TaskActivity: ActivityComponent<Data> = ({ id }) => {
  const [state, setState] = useState<PossibleTaskState | undefined>()

  const session = useCurrentSession()

  const idChanged = useRef(false)

  useEffect(() => {
    idChanged.current = true
    setTimeout(() => {
      idChanged.current = false
    }, CLOSE_TIMEOUT + 50)
  }, [id])

  const taskId = parseInt(id, 10)
  const companyId = session.company.id

  const { open, closeActivity } = useActivity()

  const { data, loading } = useQuery(TaskActivityDocument, {
    variables: {
      taskId,
      companyId,
    },
    skip: !open,
  })

  const task = data?.tasks.inboxTask

  const kind = task?.kind
  const taskable = task?.taskable
  const affectee = task?.affecteesConnection?.nodes?.[0]?.employee ?? undefined

  const category = task?.category ?? undefined
  const status = task?.status

  useEffect(() => {
    if (status) {
      trackGoToTaskDetail({
        category,
        status,
      })
    }
  }, [category, status])

  const affecteeName = affectee ? getEmployeeDisplayName(affectee) : undefined

  const taskDefinition = useTaskDefinition({
    taskableTypename: taskable?.__typename,
  })

  const safeTaskable =
    taskDefinition && taskable ? getTaskableForTypename(taskable, taskDefinition) : undefined
  const isStateSafe = !!(
    taskDefinition && getStateForTaskDefinition(state, taskDefinition.initialState)
  )

  useEffect(() => {
    if (taskDefinition) {
      setState(taskDefinition.initialState)
    }
  }, [taskDefinition])

  const title =
    taskable && affecteeName
      ? getTaskTitle({
          taskName: task.name,
          taskableInfoForTitle: taskable,
          affecteeName,
          dueOn: task.dueOn ?? undefined,
        })
      : undefined

  const createdBy = task?.author
    ? {
        firstName: task.author.preferredName || task.author.firstName,
        lastName: task.author.lastName,
        avatarUrl: buildUrl(task.author.avatar?.url),
      }
    : undefined

  const metadata = task
    ? getMetadata({
        createdBy,
        createdAt: task.createdAt,
        dueOn: task.dueOn ?? undefined,
      })
    : []

  const handleClose = useCallback(() => {
    trackCloseTask({
      category,
    })
    setTimeout(() => {
      if (!idChanged.current) {
        closeActivity()
      }
    }, CLOSE_TIMEOUT)
  }, [category, closeActivity, idChanged])

  const showLoader = !task && !taskDefinition && loading

  const statusTag = task?.status ? getStatusTag(task.status) : undefined
  const module = getModule(category)

  const moduleHref = useModuleHref(category)

  const Footer: TaskFooterComponent<never> | undefined = taskDefinition?.footer

  if (!!task && !!taskDefinition && taskDefinition.renderWithoutOneModal) {
    return (
      <Content
        affectee={affectee}
        taskDefinition={taskDefinition}
        taskable={taskable}
        taskId={taskId}
        updateState={setState}
        state={state}
      />
    )
  }

  return (
    <F0Dialog
      isOpen={open}
      onClose={handleClose}
      position="right"
      module={module && moduleHref ? { ...module, href: moduleHref } : undefined}
    >
      {!!task && !!taskDefinition ? (
        <>
          <ResourceHeader title={title ?? task.name} status={statusTag} metadata={metadata} />
          <Box paddingY="s16">
            <Content
              affectee={affectee}
              taskDefinition={taskDefinition}
              taskable={taskable}
              taskId={taskId}
              updateState={setState}
              state={state}
              dueOn={task.dueOn ?? undefined}
            />
          </Box>
          {isStateSafe && Footer && (
            <Box
              paddingTop="s16"
              border={{ top: { width: 's1', style: 'solid', color: 'grey300' } }}
            >
              <Footer
                taskId={taskId}
                taskable={safeTaskable}
                taskKind={kind ?? undefined}
                state={state}
                status={task.status ?? undefined}
              />
            </Box>
          )}
        </>
      ) : (
        showLoader && <Loading />
      )}
    </F0Dialog>
  )
}
